import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-gray-600">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to OCYM Blood Donors Network. By accessing or using our platform, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services. These Terms constitute a legally binding agreement between you and the Orthodox Christian Youth Movement (OCYM).
            </p>
          </section>

          {/* Service Description */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Service Description</h2>
            <p className="text-gray-700 mb-3">
              OCYM Blood Donors Network is a platform that connects blood donors with individuals and healthcare facilities in need of blood donations across Kerala. Our services include:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Donor registration and profile management</li>
              <li>Blood request posting and management</li>
              <li>Matching donors with recipients based on blood type and location</li>
              <li>Notification services for urgent blood requests</li>
              <li>Donation history tracking and eligibility management</li>
            </ul>
          </section>

          {/* Eligibility */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligibility</h2>
            <p className="text-gray-700 mb-3">To use our services, you must:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Be at least 18 years of age</li>
              <li>Be legally capable of entering into binding contracts</li>
              <li>Provide accurate and complete registration information</li>
              <li>Meet the medical eligibility criteria for blood donation as established by healthcare authorities</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>
          </section>

          {/* User Responsibilities */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Responsibilities</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.1 Account Security</h3>
                <p className="text-gray-700">
                  You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. Notify us immediately of any unauthorized use.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.2 Accurate Information</h3>
                <p className="text-gray-700">
                  You must provide accurate, current, and complete information during registration and keep your profile updated. Providing false information may result in account termination.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.3 Medical Eligibility</h3>
                <p className="text-gray-700">
                  You are responsible for ensuring you meet all medical eligibility requirements for blood donation. Consult with healthcare professionals before donating blood.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.4 Respectful Conduct</h3>
                <p className="text-gray-700">
                  You agree to use our platform respectfully and not to harass, abuse, or harm other users. Any inappropriate behavior may result in account suspension or termination.
                </p>
              </div>
            </div>
          </section>

          {/* Prohibited Activities */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Prohibited Activities</h2>
            <p className="text-gray-700 mb-3">You agree NOT to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Use the platform for any illegal or unauthorized purpose</li>
              <li>Impersonate any person or entity</li>
              <li>Post false, misleading, or fraudulent blood requests</li>
              <li>Sell or commercialize blood donations</li>
              <li>Harvest or collect user information without consent</li>
              <li>Interfere with or disrupt the platform's operation</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Use automated systems (bots) to access the platform</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          {/* Medical Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Medical Disclaimer</h2>
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6">
              <p className="text-gray-800 font-semibold mb-3">IMPORTANT MEDICAL DISCLAIMER:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>We are NOT a medical service provider or healthcare facility</li>
                <li>We do NOT provide medical advice, diagnosis, or treatment</li>
                <li>We do NOT verify the medical eligibility of donors</li>
                <li>We do NOT conduct blood testing or screening</li>
                <li>All blood donations must be conducted through licensed healthcare facilities</li>
                <li>Consult with qualified healthcare professionals before donating blood</li>
                <li>Follow all medical guidelines and safety protocols</li>
              </ul>
            </div>
          </section>

          {/* Liability Limitations */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
            <p className="text-gray-700 mb-3">
              To the maximum extent permitted by law:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>We provide the platform "AS IS" without warranties of any kind</li>
              <li>We are not liable for any medical complications arising from blood donations</li>
              <li>We are not responsible for the actions or conduct of users</li>
              <li>We are not liable for any direct, indirect, incidental, or consequential damages</li>
              <li>Our total liability shall not exceed the amount you paid to use our services (if any)</li>
              <li>We do not guarantee the availability, accuracy, or reliability of the platform</li>
            </ul>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Indemnification</h2>
            <p className="text-gray-700">
              You agree to indemnify, defend, and hold harmless OCYM, its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including legal fees) arising from your use of the platform, violation of these Terms, or infringement of any rights of another party.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property</h2>
            <p className="text-gray-700 mb-3">
              All content, features, and functionality of the platform, including but not limited to text, graphics, logos, icons, images, and software, are the exclusive property of OCYM and are protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-700">
              You may not copy, modify, distribute, sell, or lease any part of our services without our express written permission.
            </p>
          </section>

          {/* Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Privacy</h2>
            <p className="text-gray-700">
              Your use of our platform is also governed by our <Link to="/privacy" className="text-blue-600 hover:text-blue-700 font-semibold">Privacy Policy</Link>. Please review our Privacy Policy to understand how we collect, use, and protect your personal information.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination</h2>
            <div className="space-y-3">
              <p className="text-gray-700">
                <strong>By You:</strong> You may terminate your account at any time by contacting us or using the account deletion feature.
              </p>
              <p className="text-gray-700">
                <strong>By Us:</strong> We reserve the right to suspend or terminate your account at any time, with or without notice, for violations of these Terms, fraudulent activity, or any other reason we deem appropriate.
              </p>
              <p className="text-gray-700">
                Upon termination, your right to use the platform will immediately cease, and we may delete your account and data in accordance with our Privacy Policy.
              </p>
            </div>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Modifications to Terms</h2>
            <p className="text-gray-700">
              We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated Terms on our platform and updating the "Last updated" date. Your continued use of the platform after such changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law and Jurisdiction</h2>
            <p className="text-gray-700">
              These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms or your use of the platform shall be subject to the exclusive jurisdiction of the courts in Kerala, India.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Dispute Resolution</h2>
            <p className="text-gray-700 mb-3">
              In the event of any dispute, controversy, or claim arising from these Terms:
            </p>
            <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
              <li>Contact us first to attempt informal resolution</li>
              <li>If informal resolution fails, parties agree to mediation</li>
              <li>If mediation fails, disputes will be resolved through binding arbitration</li>
              <li>Arbitration will be conducted in Kerala, India</li>
            </ol>
          </section>

          {/* Severability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Severability</h2>
            <p className="text-gray-700">
              If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
              <div className="space-y-2">
                <p className="text-gray-800">
                  <strong>OCYM Blood Donors Network</strong>
                </p>
                <p className="text-gray-700">
                  <strong>Email:</strong> <a href="mailto:support@ocymblood.org" className="text-blue-600 hover:text-blue-700">support@ocymblood.org</a>
                </p>
                <p className="text-gray-700">
                  <strong>Phone:</strong> <a href="tel:9809034879" className="text-blue-600 hover:text-blue-700">+91 9809034879</a>
                </p>
                <p className="text-gray-700">
                  <strong>Website:</strong> <a href="https://ocymonline.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">ocymonline.org</a>
                </p>
              </div>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Acknowledgment</h2>
            <p className="text-gray-700">
              BY USING OUR PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE. IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT USE OUR SERVICES.
            </p>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
